import React, { useMemo } from "react";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import { useGetBlogByIdQuery } from "../../../services/blog/blogApiSlice";
import { CategoryPageSkeleton } from "../../../component/skeleton/CategoryPageSkeleton";
import classes from "./blogPreviewPage.module.css";

/* ── helpers (mirrored from marketplace) ── */
const slugify = (text = "") =>
  text.toLowerCase().trim().replace(/[^\w\s-]/g, "").replace(/\s+/g, "-");

const extractH1Tags = (htmlString = "") => {
  const doc = new DOMParser().parseFromString(htmlString, "text/html");
  return Array.from(doc.querySelectorAll("h1")).map((h1, i) => ({
    id: i + 1,
    content: h1.textContent.trim(),
    anchor: slugify(h1.textContent.trim()),
  }));
};

const injectH1Ids = (htmlString = "") => {
  const doc = new DOMParser().parseFromString(htmlString, "text/html");
  doc.querySelectorAll("h1").forEach((h1) => {
    h1.setAttribute("id", slugify(h1.textContent || ""));
  });
  return doc.body.innerHTML;
};

const hasContent = (data) =>
  !!(data && (data.title || data.description || data.banner_image_url));

export const BlogPreviewPage = () => {
  const { blogId } = useParams();
  const location = useLocation();
  const navigate = useNavigate();

  const isAddPreview = !blogId;
  const stateData = location.state?.blogData;

  const { data: fetchedBlog, isLoading } = useGetBlogByIdQuery(blogId, {
    skip: isAddPreview,
  });

  const blog = isAddPreview ? stateData : fetchedBlog;

  const { toc, htmlWithIds } = useMemo(() => {
    const withIds = injectH1Ids(blog?.description || "");
    return { toc: extractH1Tags(withIds), htmlWithIds: withIds };
  }, [blog?.description]);

  const handleBack = () => {
    if (isAddPreview) {
      navigate("/blogs/add", { state: { blogData: stateData } });
    } else {
      navigate(`/blogs/${blogId}`);
    }
  };

  const handleScroll = (anchor) => {
    const el = document.getElementById(anchor);
    if (!el) return;
    window.scrollTo({
      top: el.getBoundingClientRect().top + window.scrollY - 80,
      behavior: "smooth",
    });
  };

  if (!isAddPreview && isLoading) return <CategoryPageSkeleton />;

  if (!hasContent(blog)) {
    return (
      <div className={classes.emptyWrapper}>
        <div className={classes.emptyCard}>
          <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="#9ca3af" strokeWidth="1.5">
            <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
            <polyline points="14,2 14,8 20,8" />
            <line x1="16" y1="13" x2="8" y2="13" />
            <line x1="16" y1="17" x2="8" y2="17" />
          </svg>
          <h2 className={classes.emptyTitle}>Nothing to Preview</h2>
          <p className={classes.emptyMessage}>
            Fill in the blog form to see how it will appear on the marketplace knowledge center.
          </p>
          <button className={classes.backBtn} onClick={handleBack}>
            Go Back to Form
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className={classes.pageWrapper}>
      {/* ── Preview indicator bar ── */}
      <div className={classes.previewBar}>
        <span className={classes.previewBadge}>Preview Mode</span>
        <button className={classes.backBtn} onClick={handleBack}>
          &#8592; Back to {isAddPreview ? "Create" : "Edit"}
        </button>
      </div>

      {/* ── Exact KnowledgeCenterDetail layout ── */}
      <div className={classes.box}>

        {/* Banner */}
        <div className={classes.box__banner}>
          <div className={classes.box__banner__media__helper}>

            {/* Left — text */}
            <div className={classes.box__banner__intro}>
              <div className={classes.box__banner__intro__section}>
                <button className={classes.box__banner__intro__section__button}>
                  {blog?.category}
                </button>
              </div>
              <div className={classes.box__banner__intro__head}>
                <h3 className={classes.box__banner__intro__head__title}>
                  {blog?.title}
                </h3>
                <p className={classes.box__banner__intro__head__subtitle}>
                  {blog?.short_desc}
                </p>
                <p className={classes.box__banner__intro__section__date}>
                  Mobigarage Editorial &bull; {blog?.publishing_date}
                </p>
              </div>
            </div>

            {/* Right — image */}
            <div className={classes.box__banner__banner}>
              <img
                className={classes.box__banner__banner__img}
                src={blog?.banner_image_url}
                alt={blog?.title || "Blog banner"}
              />
            </div>

          </div>
        </div>

        {/* Content */}
        <div className={classes.contentLayout}>
          {toc.length > 0 && (
            <aside className={classes.tocWrapper}>
              <div className={classes.toc}>
                <div className={classes.toc__header}>
                  <h4 className={classes.toc__title}>Table of Contents</h4>
                </div>
                <div className={classes.toc__list}>
                  {toc.map((h1) => (
                    <div
                      key={h1.anchor}
                      className={classes.toc__item}
                      onClick={() => handleScroll(h1.anchor)}
                    >
                      <span className={classes.toc__index}>{h1.id}</span>
                      <span className={classes.toc__content}>{h1.content}</span>
                    </div>
                  ))}
                </div>
              </div>
            </aside>
          )}
          <div
            className={classes.box__description}
            dangerouslySetInnerHTML={{ __html: htmlWithIds }}
          />
        </div>

      </div>
    </div>
  );
};
